import { useNavigate } from 'react-router-dom';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import {
    communicationsInsightsWindowLabel,
    type CommunicationsInsightsGranularity,
    type CommunicationsInsightsTopPractice
} from '../../../services/communicationsInsights';
import { CardErrorState } from './CardErrorState';

// "Top practices" card - ranked rows with a thin proportional bar instead
// of rank numbers.
const CARD_CLASS = 'bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col h-full';
const SKELETON_ROW_COUNT = 4;

function SkeletonRow() {
    return (
        <div className="flex items-center gap-3 px-5 py-2.5">
            <div className="w-[120px] h-3 rounded bg-gray-100 animate-pulse shrink-0" />
            <div className="flex-1 h-2 rounded-full bg-gray-100 animate-pulse" />
            <div className="w-6 h-3 rounded bg-gray-100 animate-pulse shrink-0" />
        </div>
    );
}

interface PracticeRowProps {
    practice: CommunicationsInsightsTopPractice;
    maxCount: number;
    onOpen: () => void;
}

function PracticeRow({ practice, maxCount, onOpen }: PracticeRowProps) {
    const pct = maxCount > 0 ? Math.round((practice.count / maxCount) * 100) : 0;
    return (
        <button
            type="button"
            onClick={onOpen}
            className="w-full flex items-center gap-3 px-5 py-2.5 hover:bg-gray-50 text-left"
        >
            <span className="text-[12px] font-medium text-gray-600 truncate w-[120px] shrink-0">
                {practice.practiceName}
            </span>
            <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full rounded-full bg-[#2C6F67]" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-6 text-[12px] font-bold text-gray-800 tabular-nums text-right">
                {practice.count}
            </span>
        </button>
    );
}

interface TopPracticesByCommunicationsCardProps {
    topPractices: CommunicationsInsightsTopPractice[];
    granularity: CommunicationsInsightsGranularity;
    isLoading: boolean;
    isError?: boolean;
    onRetry?: () => void;
    className?: string;
}

export function TopPracticesByCommunicationsCard({
    topPractices,
    granularity,
    isLoading,
    isError = false,
    onRetry,
    className = ''
}: TopPracticesByCommunicationsCardProps) {
    const navigate = useNavigate();
    const maxCount = topPractices.reduce((max, p) => Math.max(max, p.count), 0);

    return (
        <div className={`${CARD_CLASS} ${className}`.trim()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
                <h3 className="text-[15px] font-bold text-gray-800">Top practices</h3>
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
                    {communicationsInsightsWindowLabel(granularity)}
                </span>
            </div>
            {isLoading ? (
                <div className="py-1.5 flex-1 min-h-0 overflow-y-auto scrollbar-hide">
                    {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                        <SkeletonRow key={i} />
                    ))}
                </div>
            ) : isError ? (
                <CardErrorState message="Couldn't load top practices" onRetry={() => onRetry?.()} />
            ) : topPractices.length === 0 ? (
                <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-2 px-6 py-6 text-center">
                    <BuildingOffice2Icon className="h-8 w-8 text-gray-300" strokeWidth={1} />
                    <p className="text-sm text-gray-500">No communications in this period.</p>
                </div>
            ) : (
                <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide py-1">
                    {topPractices.map((practice) => (
                        <PracticeRow
                            key={practice.practiceId}
                            practice={practice}
                            maxCount={maxCount}
                            onOpen={() => navigate(`/accounts/practices/${practice.practiceId}`)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
